import type { Components, ExtraProps } from "react-markdown";

import React from "react";

import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import { parseJsonOrNull } from "replo-utils/lib/json";
import { chatTokenSchema } from "schemas/chatMessage";

import ChatToken from "./ChatToken";

interface MarkdownRendererProps {
  children: string;
  className?: string;
}
interface ElementNode {
  type: "element";
  tagName: string;
  properties?: Record<string, unknown>;
  children?: AstNode[];
  data?: Record<string, unknown>;
}

interface TextNode {
  type: "text";
  value?: string;
}

interface ParentNode {
  type: string;
  children: AstNode[];
}

type AstNode = ElementNode | TextNode | ParentNode;

function isElementNode(node: AstNode): node is ElementNode {
  return node.type === "element";
}

function isTextNode(node: AstNode): node is TextNode {
  return node.type === "text";
}

function hasChildren(node: AstNode): node is ParentNode {
  return "children" in node && Array.isArray(node.children);
}

const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames ?? []),
    "br",
    "chat-token",
    "asset-component",
    "product-component",
  ],
  allowCustomElements: true,
  attributes: {
    ...defaultSchema.attributes,
    "chat-token": ["data-token"],
    "asset-component": ["data"],
    "product-component": ["data"],
  },
};

const rehypeChatToken = () => (tree: AstNode) => {
  transformChatTokenNodes(tree);
};

const transformChatTokenNodes = (node: AstNode): void => {
  if (isElementNode(node) && node.tagName === "chat-token") {
    const payload = readTokenPayload(node);
    if (payload) {
      node.children = [];
      node.data = {
        ...(node.data ?? {}),
        tokenPayload: payload,
      };
    }
  }

  if (hasChildren(node)) {
    node.children.forEach((child) => transformChatTokenNodes(child));
  }
};

export function MarkdownRenderer({
  children,
  className,
}: MarkdownRendererProps) {
  return (
    <Markdown
      remarkPlugins={[remarkGfm]}
      remarkRehypeOptions={{ allowDangerousHtml: true }}
      rehypePlugins={[
        rehypeRaw,
        rehypeChatToken,
        [rehypeSanitize, sanitizeSchema],
      ]}
      components={COMPONENTS}
      className={className}
    >
      {children}
    </Markdown>
  );
}

const chatTokenRenderer = ({ node }: ExtraProps) => {
  if (
    !node ||
    !isElementNode(node as AstNode) ||
    (node as ElementNode).tagName !== "chat-token"
  ) {
    return null;
  }

  const element = node as ElementNode;
  const payload = retrieveTokenPayload(element);
  if (!payload) {
    return null;
  }

  const parsed = chatTokenSchema.safeParse(parseJsonOrNull(payload));
  if (!parsed.success) {
    return null;
  }

  return (
    <span className="inline align-baseline">
      <ChatToken token={parsed.data} />
    </span>
  );
};

type ChatTokenComponents = Components & {
  "chat-token": typeof chatTokenRenderer;
};

type Renderer = NonNullable<Components[keyof Components]>;

type WithClassOptions = {
  as?: keyof JSX.IntrinsicElements;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
};

const withClass = (
  tag: keyof JSX.IntrinsicElements,
  className?: string,
  { as, prefix, suffix }: WithClassOptions = {},
): Renderer => {
  const Component = ({
    node: _node,
    children,
    ...props
  }: React.ClassAttributes<HTMLElement> &
    React.HTMLAttributes<HTMLElement> &
    ExtraProps) => {
    const Tag = as ?? tag;
    const content: React.ReactNode[] = [];

    if (prefix !== undefined && prefix !== null) {
      content.push(prefix);
    }

    content.push(children);

    if (suffix !== undefined && suffix !== null) {
      content.push(suffix);
    }

    return React.createElement(Tag, { className, ...props }, ...content);
  };

  return Component as Renderer;
};

const COMPONENTS = {
  h1: withClass("h1", "text-2xl font-semibold"),
  h2: withClass("h2", "font-semibold text-xl"),
  h3: withClass("h3", "font-semibold text-lg"),
  h4: withClass("h4", "font-semibold text-base"),
  h5: withClass("h5", "font-medium"),
  strong: withClass("strong", "font-semibold"),
  a: withClass("a", "text-primary underline underline-offset-2"),
  blockquote: withClass("blockquote", "border-l-2 border-primary pl-4"),
  code: withClass("code", "bg-surface px-1 py-0.5 rounded text-xs"),
  pre: withClass("pre"),
  ol: withClass("ol", "list-decimal space-y-2 pl-6"),
  ul: withClass("ul", "list-disc space-y-2 pl-6"),
  li: withClass("li", "my-1.5"),
  table: withClass(
    "table",
    "w-full border-collapse overflow-y-auto rounded-md border border-foreground/20",
  ),
  thead: withClass("thead"),
  tbody: withClass("tbody"),
  tr: withClass("tr", "m-0 border-t p-0 even:bg-muted"),
  th: withClass(
    "th",
    "border border-foreground/20 px-4 py-2 text-left font-bold [&[align=center]]:text-center [&[align=right]]:text-right",
  ),
  td: withClass(
    "td",
    "border border-foreground/20 px-4 py-2 text-left [&[align=center]]:text-center [&[align=right]]:text-right",
  ),
  p: withClass("p", "whitespace-pre-wrap"),
  hr: withClass("hr", "border-foreground/20"),
  "chat-token": chatTokenRenderer,
} as ChatTokenComponents;

const extractAttributeValue = (element: ElementNode): string | null => {
  const properties = element.properties ?? {};
  const attributeValue =
    properties["data-token"] ?? properties.dataToken ?? null;

  if (typeof attributeValue === "string") {
    return attributeValue;
  }

  if (Array.isArray(attributeValue) && attributeValue.length > 0) {
    const first = attributeValue[0];
    if (typeof first === "string") {
      return first;
    }
  }

  return null;
};

const readTokenPayload = (element: ElementNode) => {
  const attributeValue = extractAttributeValue(element);
  if (attributeValue) {
    return decodePayload(attributeValue);
  }

  const legacyText = (element.children ?? [])
    .map((child) => (isTextNode(child) ? child.value ?? "" : ""))
    .join("")
    .trim();

  return legacyText ? decodePayload(legacyText) : null;
};

const retrieveTokenPayload = (element: ElementNode) => {
  const payloadFromData = element.data?.tokenPayload;
  if (typeof payloadFromData === "string" && payloadFromData.length > 0) {
    return payloadFromData;
  }

  return readTokenPayload(element);
};

const decodePayload = (value: string) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};
